<template>
    <div
        role="group"
        :aria-label="$t('segmentStats.Eligible visitors')">
        <template v-if="showEligibleVisitorCount">
            <pendo-divider
                v-if="showTopDividerLine"
                aria-hidden="true" />
            <div class="stats-section">
                <span class="eligible-visitors-label">
                    {{ $t('segmentStats.Eligible visitors') }}
                    <span
                        v-pendo-tooltip="eligibleVisitorsTooltipConfig"
                        tabindex="0"
                        role="tooltip"
                        class="info-icon-wrapper"
                        :aria-label="$t('segmentStats.More information about eligible visitors')">
                        <pendo-icon
                            type="info"
                            size="14"
                            aria-describedby="eligible-visitors-tooltip-description" />
                    </span>
                    <span
                        id="eligible-visitors-tooltip-description"
                        class="sr-only">
                        {{ eligibleVisitorsTooltipText }}
                    </span>
                </span>
                <span
                    v-if="isBelowMinSegmentSize"
                    class="below-min-segment-message">
                    {{
                        $t('segmentStats.Must have at least {count} eligible visitors to use this draft segment.', {
                            count: minVisitorsInSegment
                        })
                    }}
                </span>
                <eligible-visitor-count
                    :segment="segment"
                    :is-valid="isValid"
                    :group-number="groupNumber"
                    @eligible-visitors-updated="$emit('eligible-visitors-updated', $event)" />
            </div>
        </template>
        <template v-if="showInsightsSegmentEligibility">
            <pendo-divider
                v-if="showTopDividerLine"
                aria-hidden="true" />
            <div class="stats-section">
                <span class="eligible-visitors-label">
                    {{ $t('segmentStats.Insights Eligible') }}
                    <span
                        v-pendo-tooltip="insightsEligibilityTooltipConfig"
                        class="info-icon-wrapper"
                        tabindex="0"
                        role="button"
                        :aria-label="$t('segmentStats.More information about insights eligibility')">
                        <pendo-icon
                            type="info"
                            size="14"
                            aria-describedby="insights-eligibility-tooltip-description" />
                    </span>
                    <span
                        id="insights-eligibility-tooltip-description"
                        class="sr-only">
                        {{ insightsSegmentEligibilityTooltipMessage }}
                    </span>
                </span>
                <insights-segment-eligibility
                    :segment="segment"
                    :is-valid="isValid"
                    :app-id="insightsSegmentAppId" />
            </div>
        </template>
        <template v-if="showGuideEligibility">
            <pendo-divider
                v-if="(!showEligibleVisitorCount && showTopDividerLine) || showEligibleVisitorCount"
                aria-hidden="true" />
            <div class="stats-section">
                <span class="guide-eligibility-label">
                    {{ $t('segmentStats.Guide Eligible') }}
                </span>
                <div class="guide-eligibility-wrapper">
                    <span class="guide-eligibility"> {{ guideEligibilityText }}</span>
                    <span
                        v-if="!guideEligibility.eligible"
                        v-pendo-tooltip="guideEligibilityTooltipConfig"
                        class="info-icon-wrapper"
                        tabindex="0"
                        role="button"
                        :aria-label="$t('segmentStats.Not guides eligible')">
                        <pendo-icon
                            type="info"
                            size="14"
                            aria-describedby="guide-eligibility-tooltip-description" />
                    </span>
                    <span
                        v-if="!guideEligibility.eligible"
                        id="guide-eligibility-tooltip-description"
                        class="sr-only">
                        {{ guideEligibility.reason }}
                    </span>
                </div>
            </div>
        </template>
        <template v-if="segmentRuleCount">
            <pendo-divider aria-hidden="true" />
            <div class="stats-section">
                <span class="segments-used-label">
                    {{ $t('segmentStats.Segments Used') }}
                    <span
                        v-pendo-tooltip="segmentsUsedTooltipConfig"
                        class="info-icon-wrapper"
                        tabindex="0"
                        role="button"
                        :aria-label="$t('segmentStats.More information about segments used limit')">
                        <pendo-icon
                            type="info"
                            size="14"
                            aria-describedby="segments-used-tooltip-description" />
                    </span>
                    <span
                        id="segments-used-tooltip-description"
                        class="sr-only">
                        {{ segmentsUsedTooltipMessage }}
                    </span>
                </span>
                <span class="segments-used">
                    {{
                        $t('segmentStats.{segmentRuleCount} of {ruleLimit}', {
                            segmentRuleCount: segmentRuleCount,
                            ruleLimit: SEGMENT_RULE_LIMIT
                        })
                    }}
                </span>
            </div>
        </template>
    </div>
</template>

<script>
import { mapGetters, mapState } from 'vuex';
import { PendoDivider, PendoIcon, PendoTooltip } from '@pendo/components';
import EligibleVisitorCount from '@/segments/components/segment-editor-vue/components/eligible-visitor-count';
import InsightsSegmentEligibility from '@/insights/components/insights-segment-eligibility/insights-segment-eligibility.vue';
import { SEGMENT_RULE_LIMIT } from '@/utils/segments.js';
import { getIneligibleVisitorsTooltipMessage, getIneligibleVisitorsTooltipText } from '@/segments/constants';

export default {
    name: 'SegmentStats',
    components: {
        EligibleVisitorCount,
        InsightsSegmentEligibility,
        PendoDivider,
        PendoIcon,
    },
    directives: {
        PendoTooltip,
    },
    props: {
        isValid: {
            type: Boolean,
            required: true,
        },
        segment: {
            type: Object,
            required: true,
        },
        insightsSegmentAppId: {
            type: [String, Number],
            default: undefined,
        },
        segmentRuleCount: {
            type: Number,
            required: true,
        },
        showTopDividerLine: {
            type: Boolean,
            default: true,
        },
        showEligibleVisitorCount: {
            type: Boolean,
            default: true,
        },
        showGuideEligibility: {
            type: Boolean,
            default: true,
        },
        showInsightsSegmentEligibility: {
            type: Boolean,
            default: false,
        },
        groupNumber: {
            type: Number,
            default: null,
        },
        isBelowMinSegmentSize: {
            type: Boolean,
            default: false,
        },
        minVisitorsInSegment: {
            type: Number,
            default: 0,
        },
    },
    emits: ['eligible-visitors-updated'],
    data () {
        return {
            SEGMENT_RULE_LIMIT,
            eligibleVisitorsTooltipMessage: '',
            eligibleVisitorsTooltipText: '',
        };
    },
    computed: {
        ...mapState({
            isInsightsSegmentEligible: (state) => state.segments.isInsightsSegmentEligible,
        }),
        ...mapGetters({
            getSegmentGuideEligibility: 'segments/getSegmentGuideEligibility',
        }),
        guideEligibility () {
            return this.getSegmentGuideEligibility(this.segment);
        },
        guideEligibilityText () {
            if (!this.segment) {
                return '--';
            }

            return this.guideEligibility.eligible ? this.$t('Yes') : this.$t('No');
        },
        insightsSegmentEligibilityTooltipMessage () {
            if (!this.insightsSegmentAppId) {
                return this.$t(
                    "segmentStats.To check your segment's eligibility for insights, specify which app you want insights on.",
                );
            }

            if (this.isInsightsSegmentEligible) {
                return this.$t(
                    "segmentStats.Currently, this segment is eligible and might receive insights. If it doesn't, you'll receive insights when there is enough data. If you don't see insights this week, you might still receive insights in the future.",
                );
            }

            return this.$t(
                "segmentStats.Currently, this segment isn't eligible to receive insights as there isn't enough data. You can check the rules or try again when there is more data. You can still save this segment as it might be eligible in the future.",
            );
        },
        segmentsUsedTooltipMessage () {
            return this.$t('segmentStats.The number of segments you can use is limited to {limit}.', {
                limit: SEGMENT_RULE_LIMIT,
            });
        },
        eligibleVisitorsTooltipConfig () {
            return this.createTooltipConfig({
                classes: 'eligible-visitors-info-tooltip',
                multiline: true,
                // Message is a static string

                html: true,
                content: this.eligibleVisitorsTooltipMessage,
            });
        },
        insightsEligibilityTooltipConfig () {
            return this.createTooltipConfig({
                classes: 'insights-segment-eligibility',
                multiline: true,
                content: this.insightsSegmentEligibilityTooltipMessage,
            });
        },
        guideEligibilityTooltipConfig () {
            return this.createTooltipConfig({
                multiline: true,
                content: this.guideEligibility.reason,
            });
        },
        segmentsUsedTooltipConfig () {
            return this.createTooltipConfig({
                multiline: true,
                content: this.segmentsUsedTooltipMessage,
            });
        },
    },
    created () {
        this.eligibleVisitorsTooltipMessage = getIneligibleVisitorsTooltipMessage();
        this.eligibleVisitorsTooltipText = getIneligibleVisitorsTooltipText();
    },
    methods: {
        createTooltipConfig (options) {
            const baseConfig = {
                trigger: 'hover focus',
                arrow: true,
            };

            return {
                ...baseConfig,
                ...options,
            };
        },
    },
};
</script>

<style lang="less" scoped>
.stats-section {
    padding: 0 16px;
    background: var(--palette-gray-0);

    .guide-eligibility,
    .segments-used {
        font-weight: 600;
        font-size: 22px;
        line-height: 22px;
        margin-right: 8px;
    }
}

.eligible-visitors-label,
.guide-eligibility-label,
.segments-used-label {
    display: grid;
    grid-auto-flow: column;
    grid-gap: 8px;
    justify-content: start;
    font-weight: 600;
    height: 24px;
}

.guide-eligibility-wrapper {
    display: flex;
    align-items: center;
}

.info-icon-wrapper {
    display: inline-flex;
    align-items: center;
    cursor: pointer;
}

.below-min-segment-message {
    display: block;
    font-size: 14px;
    font-weight: 400;
    line-height: 150%;
    color: var(--palette-gray-70);
}

.eligible-visitors-info-tooltip {
    .tooltip-inner {
        max-width: unset !important;
        ul {
            padding-inline-start: 20px;
            margin: 0;
        }
    }
}
</style>
